import React from 'react';
import {useIntl} from '@sail/intl';
import {V1Provider} from '@stripe-internal/data-v1';

import Loading from 'src/components/core/Loading';
import {useResumeReturnDetailsCollectionSession} from 'src/return/api/useResumeReturnDetailsCollectionSession';
import {ReturnDetailsCollectionContext} from 'src/return/state/useReturnDetailsCollectionContext';
import {UserFacingError} from 'src/utils/error';
import messages from 'src/utils/messages';

const errorForStatus = (status: number, intl: ReturnType<typeof useIntl>) => {
  switch (status) {
    case 401:
    case 403:
    case 404:
      return new UserFacingError(intl.formatMessage(messages.error404));
    case 410:
      return new UserFacingError(intl.formatMessage(messages.error410));
    default:
      return new Error(`Unexpected status: ${status}`);
  }
};

export const ReturnDetailsCollectionContextProvider = ({
  children,
  sessionHash,
}: {
  children: React.ReactNode;
  sessionHash: string;
}) => {
  const intl = useIntl();
  const {data, error, loading, status} =
    useResumeReturnDetailsCollectionSession(sessionHash);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    throw error;
  }

  if (status && status !== 200) {
    throw errorForStatus(status, intl);
  }

  if (!data || !data.publishableKey) {
    throw errorForStatus(404, intl);
  }

  return (
    <ReturnDetailsCollectionContext.Provider value={data}>
      <V1Provider apiKey={data.publishableKey}>{children}</V1Provider>
    </ReturnDetailsCollectionContext.Provider>
  );
};
